import React, { Component } from 'react';
import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';

// "Kitchen" here is a single bucket, not a per-station list — GetresActionAllList
// carries no real kitchen-name master (menuitemkitchenid/kitchenid only exist as
// a grouping fallback key, see KOTDashboard.js's kotStationKey), so there's no
// data to split "South Indian kitchen" from "Chinese kitchen" by name. What IS
// real: individual live KOT tickets (grouped by kotnumber, same as KOTDashboard's
// own Kitchen tab) and their elapsed time/items — that's what expanding shows.
const elapsedFrom = (dateStr, nowMs) => {
    if (!dateStr) return '';
    const diff = nowMs - new Date(dateStr).getTime();
    if (diff < 0) return '0m';
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    return rem > 0 ? `${hrs}h ${rem}m` : `${hrs}h`;
};

class ResourceEngagementModal extends Component {

    state = {
        now: Date.now(),
        expandedKitchens: [],
        expandedKots: [],
        expandedWaiters: [],
    };

    componentDidMount() {
        // Ticks the elapsed-time labels while the modal is open — same
        // client-side-timer approach TableSelection.js's getElapsed uses.
        this._timer = setInterval(() => this.setState({ now: Date.now() }), 30000);
    }

    componentWillUnmount() {
        clearInterval(this._timer);
    }

    toggleKitchen = (key) => {
        this.setState(prev => ({
            expandedKitchens: prev.expandedKitchens.includes(key)
                ? prev.expandedKitchens.filter(k => k !== key)
                : [...prev.expandedKitchens, key],
        }));
    };

    toggleKot = (key) => {
        this.setState(prev => ({
            expandedKots: prev.expandedKots.includes(key)
                ? prev.expandedKots.filter(k => k !== key)
                : [...prev.expandedKots, key],
        }));
    };

    toggleWaiter = (key) => {
        this.setState(prev => ({
            expandedWaiters: prev.expandedWaiters.includes(key)
                ? prev.expandedWaiters.filter(k => k !== key)
                : [...prev.expandedWaiters, key],
        }));
    };

    // Two levels: kitchen (menuitemkitchenid/kitchenid, resolved to a real
    // name via the Kitchen master) -> the live KOT tickets routed to it.
    renderKitchenTab() {
        const { kitchenGroups = [] } = this.props;
        const { now, expandedKitchens, expandedKots } = this.state;

        if (kitchenGroups.length === 0) {
            return <div className="captain-kot-empty">No live KOTs in the kitchen right now.</div>;
        }

        return (
            <div className="captain-resource-body">
                {kitchenGroups.map(kitchen => {
                    const expanded = expandedKitchens.includes(kitchen.kitchenId);
                    return (
                        <div className="captain-resource-item" key={kitchen.kitchenId}>
                            <button
                                type="button"
                                className="captain-resource-item-header"
                                onClick={() => this.toggleKitchen(kitchen.kitchenId)}
                                aria-expanded={expanded}
                            >
                                <i className={`pi ${expanded ? 'pi-chevron-down' : 'pi-chevron-right'}`} />
                                <span className="captain-resource-item-label">{kitchen.kitchenName}</span>
                                <span className="captain-resource-count">
                                    {kitchen.kots.length} live KOT{kitchen.kots.length !== 1 ? 's' : ''}
                                </span>
                            </button>

                            {expanded && (
                                <div className="captain-resource-item-body">
                                    {kitchen.kots.map(group => {
                                        const kotExpanded = expandedKots.includes(group.key);
                                        return (
                                            <div className="captain-resource-subitem" key={group.key}>
                                                <button
                                                    type="button"
                                                    className="captain-resource-subitem-header"
                                                    onClick={() => this.toggleKot(group.key)}
                                                    aria-expanded={kotExpanded}
                                                >
                                                    <i className={`pi ${kotExpanded ? 'pi-chevron-down' : 'pi-chevron-right'}`} />
                                                    <span className="captain-resource-item-label">KOT {group.kotNo || '—'}</span>
                                                    {group.tableNo && (
                                                        <span className="captain-resource-item-meta">Table {group.tableNo}</span>
                                                    )}
                                                    <span className="captain-resource-item-elapsed">
                                                        {elapsedFrom(group.earliest, now)}
                                                    </span>
                                                </button>

                                                {kotExpanded && (
                                                    <div className="captain-resource-subitem-body">
                                                        {group.items.map(it => (
                                                            <div key={it.id} className="captain-resource-sub-row">
                                                                <span className="captain-kot-item-qty">&times;{it.qty}</span>
                                                                <span className="captain-kot-item-name">{it.name}</span>
                                                                <span className="captain-resource-sub-time">{it.createdTime}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    // waiterGroups come from the table master's static `waiter` field
    // (TableMaster's own assignment), paired with GetrestableOrderNum's
    // duration for that table — not a per-order confirmed assignment, since
    // the per-order Waiter field only lives inside that one order's own
    // RequestAttributesDetails (not bulk-readable). See API_MAPPING.md.
    renderWaiterTab() {
        const { waiterGroups = [] } = this.props;
        const { expandedWaiters } = this.state;

        if (waiterGroups.length === 0) {
            return <div className="captain-kot-empty">No tables currently in service.</div>;
        }

        return (
            <div className="captain-resource-body">
                {waiterGroups.map(group => {
                    const key = group.waiterId || '__unassigned';
                    const expanded = expandedWaiters.includes(key);
                    return (
                        <div className="captain-resource-item" key={key}>
                            <button
                                type="button"
                                className="captain-resource-item-header"
                                onClick={() => this.toggleWaiter(key)}
                                aria-expanded={expanded}
                            >
                                <i className={`pi ${expanded ? 'pi-chevron-down' : 'pi-chevron-right'}`} />
                                <span className="captain-resource-item-label">{group.waiterName}</span>
                                <span className="captain-resource-item-meta">
                                    {group.tables.length} table{group.tables.length !== 1 ? 's' : ''}
                                </span>
                            </button>

                            {expanded && (
                                <div className="captain-resource-item-body">
                                    {group.tables.map(t => (
                                        <div key={t.tableNo} className="captain-resource-sub-row">
                                            <span className="captain-resource-item-meta captain-resource-sub-table">
                                                Table {t.tableNo}
                                            </span>
                                            <span className="captain-kot-item-name">{t.orderNo || ''}</span>
                                            <span className="captain-resource-sub-time">{t.duration || ''}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    render() {
        const { visible, onHide, kitchenGroups = [], waiterGroups = [] } = this.props;
        const totalTables = waiterGroups.reduce((sum, g) => sum + g.tables.length, 0);
        const totalLiveKots = kitchenGroups.reduce((sum, k) => sum + k.kots.length, 0);

        const kitchenHeader = (
            <span className="captain-resource-tab-header">
                Kitchen
                <span className="captain-resource-count">
                    {totalLiveKots} live
                </span>
            </span>
        );

        const waiterHeader = (
            <span className="captain-resource-tab-header">
                Waiters
                <span className="captain-resource-count">
                    {totalTables} table{totalTables !== 1 ? 's' : ''}
                </span>
            </span>
        );

        return (
            <Dialog
                header="Resource Engagement"
                visible={visible}
                onHide={onHide}
                className="captain-resource-modal"
                style={{ width: '560px' }}
                dismissableMask
            >
                <TabView className="captain-resource-tabs">
                    <TabPanel header={kitchenHeader}>
                        {this.renderKitchenTab()}
                    </TabPanel>
                    <TabPanel header={waiterHeader}>
                        {this.renderWaiterTab()}
                    </TabPanel>
                </TabView>
            </Dialog>
        );
    }
}

export default ResourceEngagementModal;
